import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
// Defer loading of socket.io-client until we actually need a connection
import type { Socket } from 'socket.io-client';
import { SocketEvent, SocketEventHandler } from '../models/Socket';
import { AppContext } from '../window';

const ENDPOINT = "/";

export interface SocketContextType {
  socket: Socket | null;
  subscribeToEvent: <T = any>(channel: string, handler: SocketEventHandler<T>) => () => void;
  unsubscribeFromEvent: <T = any>(channel: string, handler: SocketEventHandler<T>) => void;
}

export const SocketContext = createContext<SocketContextType>({
  socket: null,
  subscribeToEvent: () => () => {},
  unsubscribeFromEvent: () => {}
});

export const useSocketContext = () => useContext(SocketContext);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const appContext = useContext(AppContext);
  
  // Store subscriptions in a ref to avoid re-renders
  const subscriptions = useRef<Map<string, Set<SocketEventHandler<any>>>>(new Map());

  // Subscribe to event
  const subscribeToEvent = useCallback(function<T = any>(channel: string, handler: SocketEventHandler<T>) {
    if (!subscriptions.current.has(channel)) {
      subscriptions.current.set(channel, new Set());
    }
    subscriptions.current.get(channel)!.add(handler as SocketEventHandler<any>);

    // Return unsubscribe function
    return () => {
      unsubscribeFromEvent(channel, handler);
    };
  }, []);

  // Unsubscribe from event
  const unsubscribeFromEvent = useCallback(function<T = any>(channel: string, handler: SocketEventHandler<T>) {
    const channelSubs = subscriptions.current.get(channel);
    if (channelSubs) {
      channelSubs.delete(handler as SocketEventHandler<any>);
      if (channelSubs.size === 0) {
        subscriptions.current.delete(channel);
      }
    }
  }, []);

  useEffect(() => {
    let isCancelled = false;
    let socketInstance: Socket | null = null;

    async function init() {
      // Only initialize socket connection for authenticated users
      if (!appContext.user?.id || !appContext.user?.credentials?.apiToken) {
        return;
      }
      try {
        const { io } = await import(/* webpackChunkName: "socket.io-client" */ 'socket.io-client');
        if (isCancelled) return;

        socketInstance = io(ENDPOINT, {
          query: { 
            id: appContext.user.id,
            siteId: appContext.siteId, 
            token: appContext.user.credentials.apiToken 
          },
          transports: ['websocket', 'polling'],
          upgrade: true,
          rememberUpgrade: true,
          timeout: 20000,
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          reconnectionAttempts: 5,
          randomizationFactor: 0.5
        });

        if (isCancelled) {
          socketInstance.disconnect();
          return;
        }
        setSocket(socketInstance);

        socketInstance.on("event", (event: SocketEvent) => {
          const channelSubs = subscriptions.current.get(event.channel);
          if (channelSubs) {
            channelSubs.forEach(handler => handler(event));
          }
        });

        socketInstance.on('connect', () => {
          console.debug('Socket connected');
        });
  
        socketInstance.on('disconnect', (reason) => {
          console.debug('Socket disconnected:', reason);
          if (reason === 'io server disconnect') {
            socketInstance && socketInstance.connect();
          }
        });
  
        socketInstance.on('connect_error', (error) => {
          console.warn('Socket connection error:', error);
        });
  
        socketInstance.on('reconnect', (attemptNumber) => {
          console.debug('Socket reconnected after', attemptNumber, 'attempts');
        });
  
        socketInstance.on('reconnect_failed', () => {
          console.warn('Socket failed to reconnect after maximum attempts');
        });
      } catch (e) {
        console.warn('Failed to load socket.io-client dynamically', e);
      }
    }

    init();

    return () => {
      isCancelled = true;
      if (socketInstance) {
        socketInstance.disconnect();
      }
    };
  }, [appContext.user?.id, appContext.siteId, appContext.user?.credentials?.apiToken]);

  const contextValue: SocketContextType = {
    socket,
    subscribeToEvent,
    unsubscribeFromEvent,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// Custom hook for components to easily subscribe to socket events
export function useSocketEvent<T = any>(channel: string, handler: SocketEventHandler<T>) {
  const { subscribeToEvent } = useSocketContext();
  
  useEffect(() => {
    const unsubscribe = subscribeToEvent<T>(channel, handler);
    return unsubscribe;
  }, [channel, handler, subscribeToEvent]);
}